import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ArrowDownward,
  CheckCircle,
  PlayArrow,
  Star,
  TrendingUp,
  PointOfSale,
  Layers,
  CalendarMonth,
  CreditCard,
  SyncAlt,
  Favorite,
  ElectricBolt,
  MonetizationOn,
  Receipt,
  People,
} from '@mui/icons-material';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Logo } from '../components/Logo';
import { LiveDemo } from '../components/LiveDemo';
import { MagicBackground } from '../components/MagicBackground';
import { useNavigate } from 'react-router-dom';

// Rotating taglines - Updated for "Playful/Interactive" vibe
const taglines = [
  "Watch Your Business Run Itself.",
  "Your 24/7 Super Employee.",
  "Turn Sleep Into Sales.",
  "Magic for Your Business.",
];

// Rotating keywords for the headline
const magicWords = ["Scheduling", "Support", "Sales", "Growth"];

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTagline, setCurrentTagline] = useState(0);
  const [currentMagicWord, setCurrentMagicWord] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const heroRef = useRef<HTMLDivElement>(null);
  useInView(heroRef, { once: true, amount: 0.3 });
  const { scrollYProgress } = useScroll({ target: heroRef });

  // Rotate taglines and magic words
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
      setCurrentMagicWord((prev) => (prev + 1) % magicWords.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Parallax effects (values computed but not used yet - reserved for future animations)
  useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  const features = [
    {
      icon: <Favorite sx={{ fontSize: 48, color: '#00D4FF' }} />,
      title: 'A Clone of Your Best Employee',
      description: 'Your AI learns your voice, your rules, and your vibe. It protects your brand reputation in every chat, just like you would.',
      color: '#00D4FF',
    },
    {
      icon: <ElectricBolt sx={{ fontSize: 48, color: '#FF6B6B' }} />,
      title: 'Replies in Seconds, Not Hours',
      description: 'Customers hate waiting. Your AI replies instantly, 24/7. It\'s like having a receptionist on Red Bull, but polite.',
      color: '#FF6B6B',
    },
    {
      icon: <MonetizationOn sx={{ fontSize: 48, color: '#00C896' }} />,
      title: 'It Upsells. You Chill.',
      description: 'It doesn\'t just answer—it books, upsells, and follows up. Watch your revenue climb.',
      color: '#00C896',
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { type: "spring", stiffness: 50, damping: 20 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0e27', color: '#ffffff', position: 'relative', overflowX: 'hidden' }}>
      {/* Fixed Navigation */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(10, 14, 39, 0.85)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1.5 }}>
            <Logo size="medium" />
            
            {isMobile ? (
              <IconButton onClick={() => setMobileMenuOpen(true)} sx={{ color: '#00D4FF' }}>
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{
                    textTransform: 'none',
                    borderColor: 'rgba(0, 212, 255, 0.5)',
                    color: '#00D4FF',
                    borderWidth: 2,
                    px: 3,
                    borderRadius: 3,
                    '&:hover': { borderColor: '#0066FF', borderWidth: 2, bgcolor: 'rgba(0, 212, 255, 0.1)' },
                  }}
                >
                  Login
                </Button>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/signup')}
                    sx={{
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)',
                      px: 3,
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(0, 102, 255, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0052CC 0%, #00B8E6 100%)',
                        boxShadow: '0 12px 32px rgba(0, 102, 255, 0.5)',
                      },
                    }}
                  >
                    Start Free
                  </Button>
                </motion.div>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Menu */}
      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} PaperProps={{ sx: { bgcolor: '#0a0e27', color: '#fff' } }}>
        <Box sx={{ width: 280, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} sx={{ color: '#fff' }}>
                <ListItemText primary="Login" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }} sx={{ color: '#00D4FF' }}>
                <ListItemText primary="Start Free" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Hero Section */}
      <Box
        ref={heroRef}
        id="hero"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          pt: 10,
        }}
      >
        <MagicBackground />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 50 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '3rem', md: '5rem' },
                    fontWeight: 900,
                    lineHeight: 1.1,
                    mb: 3,
                    letterSpacing: '-0.02em',
                    textShadow: '0 20px 80px rgba(0, 102, 255, 0.15)', // Enhance readability
                  }}
                >
                  <Box component="span" sx={{ color: '#ffffff', display: 'block' }}>
                    Automate Your
                  </Box>
                  <Box
                    sx={{
                      height: { xs: '3.5rem', md: '5.5rem' },
                      overflow: 'hidden',
                      display: 'block',
                    }}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentMagicWord}
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -40, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Box
                          component="span"
                          sx={{
                            background: 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            display: 'block',
                            pb: 1, // Space for descenders
                          }}
                        >
                          {magicWords[currentMagicWord]}
                        </Box>
                      </motion.div>
                    </AnimatePresence>
                  </Box>
                </Typography>

                {/* Rotating Tagline with improved contrast */}
                <Box sx={{ height: 60, mb: 5 }}>
                  <motion.div
                    key={currentTagline}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                        fontWeight: 400,
                        color: 'rgba(255, 255, 255, 0.9)', // Increased brightness
                        lineHeight: 1.6,
                      }}
                    >
                      {taglines[currentTagline]}
                    </Typography>
                  </motion.div>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/signup')}
                      sx={{
                        textTransform: 'none',
                        fontSize: 18,
                        py: 2,
                        px: 5,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #0066FF 0%, #00D4FF 100%)',
                        boxShadow: '0 10px 30px rgba(0, 102, 255, 0.4)',
                        fontWeight: 700,
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      Start Free Trial
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => scrollToSection('demo')}
                      startIcon={<PlayArrow />}
                      sx={{
                        textTransform: 'none',
                        fontSize: 18,
                        py: 2,
                        px: 5,
                        borderRadius: 3,
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        borderWidth: 2,
                        fontWeight: 700,
                        backdropFilter: 'blur(10px)',
                        bgcolor: 'rgba(255,255,255,0.05)',
                        '&:hover': {
                          borderColor: '#00D4FF',
                          borderWidth: 2,
                          bgcolor: 'rgba(0, 212, 255, 0.1)',
                        },
                      }}
                    >
                      Try the Magic
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>
            </Grid>
            
            {/* Hero Visual - Enhanced "Magic" Animation */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                style={{ position: 'relative' }}
              >
                {/* Abstract UI Representation */}
                 <Box
                  sx={{
                    position: 'relative',
                    height: 500,
                    width: '100%',
                    perspective: '1000px', // 3D effect
                  }}
                >
                    {/* Glowing Center Ring */}
                    <motion.div
                      animate={{ opacity: [0.5, 0.8, 0.5], scale: [1, 1.05, 1] }}
                      transition={{ duration: 4, repeat: Infinity }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(0, 102, 255, 0.1) 0%, transparent 70%)',
                        zIndex: 0,
                      }}
                    />

                    {/* Floating elements simulating messages with improved glassmorphism */}
                    {[1, 2, 3].map((i) => (
                        <motion.div
                            key={i}
                            animate={{ 
                              y: [0, -20, 0],
                              rotateX: [0, 5, 0], // Subtle 3D tilt
                              rotateY: [0, 5, 0]
                            }}
                            transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.5 }}
                            style={{
                                position: 'absolute',
                                left: `${15 + (i * 15)}%`,
                                top: `${20 + (i * 12)}%`,
                                zIndex: i,
                                width: '65%',
                            }}
                        >
                             <Paper 
                              elevation={10}
                              sx={{ 
                                p: 2.5, 
                                borderRadius: 4, 
                                bgcolor: 'rgba(20, 25, 45, 0.7)', // Darker glass
                                backdropFilter: 'blur(20px)', 
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                                position: 'relative',
                                overflow: 'hidden',
                              }}
                            >
                                {/* Shine effect */}
                                <Box sx={{ 
                                  position: 'absolute', 
                                  top: 0, 
                                  left: 0, 
                                  right: 0, 
                                  height: '1px', 
                                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' 
                                }} />

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: i === 1 ? '#FF69B4' : i === 2 ? '#00D4FF' : '#FF4500', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {i === 1 ? <Star sx={{ color: 'white', fontSize: 20 }} /> : i === 2 ? <CheckCircle sx={{ color: 'white', fontSize: 20 }} /> : <TrendingUp sx={{ color: 'white', fontSize: 20 }} />}
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Box sx={{ height: 10, width: '40%', bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1, mb: 1 }} />
                                    <Box sx={{ height: 8, width: '70%', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }} />
                                  </Box>
                                </Box>
                                <Box sx={{ height: 8, width: '90%', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, mb: 1 }} />
                                <Box sx={{ height: 8, width: '60%', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }} />
                             </Paper>
                        </motion.div>
                    ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Live Demo Section */}
      <Box id="demo" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0f1529', position: 'relative', overflow: 'hidden' }}>
        <MagicBackground color="#7B61FF" blobColors={['rgba(123, 97, 255, 0.1)', 'rgba(0, 212, 255, 0.05)']} />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Box textAlign="center" mb={8}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '4rem' },
                    fontWeight: 900,
                    mb: 3,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    Pick a Business, See it Work
                  </Box>
                </Typography>
                <Typography variant="h5" color="rgba(255, 255, 255, 0.7)" sx={{ maxWidth: 800, mx: 'auto' }}>
                  No signup needed. Just click a vibe and start chatting with our AI.
                </Typography>
              </Box>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <LiveDemo />
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Narrative Section */}
      <Box id="story" sx={{ py: { xs: 12, md: 20 }, bgcolor: '#0a0e27', position: 'relative', overflow: 'hidden' }}>
        <MagicBackground color="#FF6B6B" blobColors={['rgba(255, 107, 107, 0.08)', 'rgba(255, 69, 0, 0.05)']} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <Typography variant="h3" fontWeight={800} mb={4} lineHeight={1.2}>
                  You're The Visionary.<br />
                  <span style={{ color: '#FF6B6B' }}>Stop Being The Receptionist.</span>
                </Typography>
                <Typography variant="body1" fontSize={18} color="rgba(255,255,255,0.7)" mb={4} lineHeight={1.8}>
                  You didn't start a business to answer "How much is a cut?" at 11 PM. Let ReachOut handle the noise so you can focus on the craft.
                  <br /><br />
                  Imagine waking up to appointments booked while you slept. Imagine every customer getting a "Hello!" instantly, even when you're busy with a client.
                </Typography>
                <Button
                  variant="text"
                  endIcon={<ArrowDownward />}
                  onClick={() => scrollToSection('features')}
                  sx={{ color: '#00D4FF', fontSize: 18, fontWeight: 700, textTransform: 'none', p: 0 }}
                >
                  See how it works
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
               {/* Decorative Element */}
               <Box
                sx={{
                  position: 'relative',
                  p: 4,
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.02)',
                }}
              >
                {[
                  { title: "2:00 AM", desc: "Customer asks for pricing" },
                  { title: "2:01 AM", desc: "ReachOut answers instantly" },
                  { title: "2:02 AM", desc: "Appointment booked & deposit paid" },
                  { title: "7:00 AM", desc: "You wake up to new revenue" },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 3, mb: 3, opacity: 0.8 }}>
                     <Typography variant="h6" color="#00D4FF" sx={{ minWidth: 80 }}>{item.title}</Typography>
                     <Typography variant="h6" color="white">{item.desc}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Box id="features" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0f1529', position: 'relative', overflow: 'hidden' }}>
        <MagicBackground color="#00C896" blobColors={['rgba(0, 200, 150, 0.08)', 'rgba(0, 212, 255, 0.05)']} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
           <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -10 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 4,
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: feature.color,
                          boxShadow: `0 10px 40px ${feature.color}30`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box mb={3} sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 3, display: 'inline-block' }}>
                            {feature.icon}
                        </Box>
                        <Typography variant="h5" fontWeight={700} mb={2} sx={{ color: 'white' }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Integrations Section */}
      <Box id="integrations" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0a0e27', position: 'relative', overflow: 'hidden' }}>
        <MagicBackground color="#FFD700" blobColors={['rgba(255, 215, 0, 0.1)', 'rgba(255, 165, 0, 0.1)']} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box textAlign="center" mb={10}>
                <motion.div variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true }}>
                  <Typography variant="h2" fontWeight={800} mb={2}>
                    Your Current Stack? <span style={{color: '#FFD700'}}>We're BFFs.</span>
                  </Typography>
                  <Typography variant="h5" color="rgba(255,255,255,0.6)" mb={1}>
                    (But we can also fly solo.)
                  </Typography>
                  <Typography variant="body1" color="rgba(255,255,255,0.5)" maxWidth={600} mx="auto">
                     Whether you're married to your POS or looking for a fling with something new, we've got you covered.
                  </Typography>
                </motion.div>
            </Box>
            
            <Grid container spacing={6}>
                {/* Integration Path: "The Sidekick" */}
                <Grid item xs={12} md={6}>
                  <motion.div whileHover={{ y: -5 }} transition={{ type: 'spring', stiffness: 300 }}>
                    <Card sx={{ 
                        height: '100%',
                        p: 4, 
                        borderRadius: 5, 
                        bgcolor: 'rgba(255, 255, 255, 0.03)', 
                        border: '1px solid rgba(255, 215, 0, 0.2)', 
                        backdropFilter: 'blur(20px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(255, 215, 0, 0.1)', borderRadius: 3, color: '#FFD700' }}>
                                <SyncAlt fontSize="large" />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={700} color="white">The Power-Up</Typography>
                                <Typography variant="caption" color="rgba(255,255,255,0.5)">Keep your tools. Add the magic.</Typography>
                            </Box>
                        </Box>
                        <Typography variant="body1" color="rgba(255,255,255,0.7)" mb={4} lineHeight={1.8}>
                            Love your current setup? Great. We integrate seamlessly with Square, Clover, Stripe, and your Calendars. We don't want to replace them; we just want to make them smarter.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, opacity: 0.7 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <PointOfSale />
                                <Typography variant="caption">POS</Typography>
                            </Box>
                             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <CalendarMonth />
                                <Typography variant="caption">Calendar</Typography>
                            </Box>
                             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <CreditCard />
                                <Typography variant="caption">Payment</Typography>
                            </Box>
                        </Box>
                    </Card>
                  </motion.div>
                </Grid>

                {/* Standalone Path: "The Hero" */}
                <Grid item xs={12} md={6}>
                  <motion.div whileHover={{ y: -5 }} transition={{ type: 'spring', stiffness: 300 }}>
                    <Card sx={{ 
                        height: '100%',
                        p: 4, 
                        borderRadius: 5, 
                        bgcolor: 'rgba(255, 255, 255, 0.03)', 
                        border: '1px solid rgba(0, 212, 255, 0.2)', 
                        backdropFilter: 'blur(20px)',
                         boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(0, 212, 255, 0.1)', borderRadius: 3, color: '#00D4FF' }}>
                                <Layers fontSize="large" />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={700} color="white">The Full Stack</Typography>
                                <Typography variant="caption" color="rgba(255,255,255,0.5)">Starting fresh? We do it all.</Typography>
                            </Box>
                        </Box>
                        <Typography variant="body1" color="rgba(255,255,255,0.7)" mb={4} lineHeight={1.8}>
                            Ditch the 5 different subscriptions. ReachOut comes with Invoicing, Payments, and Scheduling built right in. One login, one price, infinite power.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, opacity: 0.7 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <Receipt />
                                <Typography variant="caption">Invoicing</Typography>
                            </Box>
                             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <People />
                                <Typography variant="caption">CRM</Typography>
                            </Box>
                             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <CalendarMonth />
                                <Typography variant="caption">Booking</Typography>
                            </Box>
                        </Box>
                    </Card>
                  </motion.div>
                </Grid>
            </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 8, bgcolor: '#050714', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid item xs={12} md={5}>
              <Logo size="small" />
              <Typography variant="body2" color="rgba(255,255,255,0.5)" sx={{ mt: 2, mb: 4, lineHeight: 1.8, maxWidth: 300 }}>
                Automating customer interactions for modern businesses. 
                Your 24/7 AI employee that never sleeps.
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.3)">
                © 2025 ReachOut AI. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="subtitle2" fontWeight={700} color="white" mb={3}>Product</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {['Features', 'Live Demo', 'Pricing', 'Integrations'].map((item) => (
                  <Typography 
                    key={item} 
                    variant="body2" 
                    color="rgba(255,255,255,0.6)" 
                    sx={{ cursor: 'pointer', '&:hover': { color: '#00D4FF' }, transition: 'color 0.2s' }}
                    onClick={() => scrollToSection(item.toLowerCase().replace(' ', ''))}
                  >
                    {item}
                  </Typography>
                ))}
                <Typography 
                  variant="body2" 
                  color="rgba(255,255,255,0.6)" 
                  sx={{ cursor: 'pointer', '&:hover': { color: '#00D4FF' }, transition: 'color 0.2s' }}
                  onClick={() => navigate('/login')}
                >
                  Login
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="subtitle2" fontWeight={700} color="white" mb={3}>Legal & Support</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography 
                  variant="body2" 
                  color="rgba(255,255,255,0.6)" 
                  sx={{ cursor: 'pointer', '&:hover': { color: '#00D4FF' }, transition: 'color 0.2s' }}
                  onClick={() => navigate('/privacy')}
                >
                  Privacy Policy
                </Typography>
                <Typography 
                  variant="body2" 
                  color="rgba(255,255,255,0.6)" 
                  sx={{ cursor: 'pointer', '&:hover': { color: '#00D4FF' }, transition: 'color 0.2s' }}
                  onClick={() => navigate('/terms')}
                >
                  Terms of Service
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.6)" sx={{ mt: 2 }}>
                  Reachout AI Corporation
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.6)">
                  1500 N GRANT ST # 7298
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.6)">
                  DENVER, CO 80203
                </Typography>
                <Typography 
                  variant="body2" 
                  color="rgba(0, 212, 255, 0.8)" 
                  sx={{ cursor: 'pointer', '&:hover': { color: '#00D4FF' }, transition: 'color 0.2s' }}
                  component="a"
                  href="mailto:support@reachoutaiapp.com"
                >
                  support@reachoutaiapp.com
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
